// The pin of a client location's map (views/clients/client-location-edit.js). A new location opens on its
// country (the account's, else the client's), with the pin at the country's centre; the admin searches for the
// address, drags the pin or clicks the map to the exact spot of the site, and it is not saved until they have. An
// existing location opens on its saved pin. The location's own country (and so its time zone) follows the pin.

export const PIN_HINT = 'Drag the pin, or click the map, to mark the exact spot of the site.';
export const PIN_NOT_MOVED = 'Please move the pin to the exact spot of the site.';

// Zoom of an existing location's saved pin, close enough to drag it to the exact spot
export const SITE_ZOOM = 15;

function code(value) {
	return typeof value === 'string' && /^[A-Za-z]{2}$/.test(value.trim()) ? value.trim().toUpperCase() : null;
}

// The country a new location's map opens on: the account's, else the client's; null when neither is known
export function startCountry(accountCountry, clientCountry) {
	return code(accountCountry) || code(clientCountry);
}

// The refusal of a Save, or null: a new location's pin must have been moved
export function pinRefusal(isNew, pinMoved) {
	return isNew && !pinMoved ? PIN_NOT_MOVED : null;
}

// A typed latitude and longitude, as numbers, or null while they are not a valid spot
export function typedSpot(latitude, longitude) {
	const blank = (v) => v === null || v === undefined || String(v).trim() === '';
	if (blank(latitude) || blank(longitude)) {
		return null;
	}
	const lat = Number(latitude);
	const lng = Number(longitude);
	if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
		return null;
	}
	return { lat, lng };
}

// A spot from the map, rounded to 6 decimals (about 10 cm)
export function roundedSpot(lat, lng) {
	return { lat: Number(Number(lat).toFixed(6)), lng: Number(Number(lng).toFixed(6)) };
}

// The country code ("GB") of a Google Geocoder answer: the pin's spot looked up, or a place searched for; null when
// the answer has none (the sea)
export function geocodeCountry(results) {
	for (const result of Array.isArray(results) ? results : []) {
		for (const part of (result && result.address_components) || []) {
			if (part && Array.isArray(part.types) && part.types.indexOf('country') !== -1) {
				return code(part.short_name);
			}
		}
	}
	return null;
}

export const SEARCH_PLACEHOLDER = 'Search for an address or place';
export const SEARCH_NOT_FOUND = 'That address or place was not found. Try adding the town or country, or drag the pin.';

// The line under Country when the pin is in another country than the one chosen: the country is changed to match
export function countryFromPinNote(countryName) {
	return countryName ? 'Country set to ' + countryName + ' from the map pin.' : '';
}

// What the admin is told after saving a location whose time zone changed while it has duties still to come (the
// server's message.zone_change; null: nothing to say). The duties' stored times are never changed.
export function zoneChangeMessage(change) {
	if (!change || !change.future_duties) {
		return null;
	}
	const count = Number(change.future_duties);
	const one = count === 1;
	const duties = one
		? 'Its 1 duty still to come keeps the same moment in time, so its start time on the clock has moved.'
		: 'Its ' + count + ' duties still to come keep the same moments in time, so their start times on the clock have moved.';
	const example = change.example && change.example.before && change.example.after && change.example.before !== change.example.after
		? ' For example, the next one started at ' + change.example.before + ' and now starts at ' + change.example.after + '.'
		: '';
	return "This location's time zone changed from " + change.from + ' to ' + change.to + '. ' + duties + example
		+ (one ? ' Please check it and edit it if it should start at a different time.' : ' Please check them and edit any that should start at a different time.');
}
